import React from 'react'
import { ConnectionField } from './connection-field'
import { ChannelPicker } from './channel-picker'

const inputType = { string: 'text', url: 'url', int: 'number', secret: 'password' }

// A Connect type's two fields (slack_app): a connected workspace, and a
// channel of it. Always needed, whatever `required` says.
const CONNECT_FIELDS = ['connection', 'channel']

// validateSchema returns { key: message } for required and pattern problems.
export const validateSchema = (schema, value, isNew) => {
  const out = {}
  schema.forEach(f => {
    if (f.type === 'email_list' || f.generate) return
    const v = value[f.key]
    const empty = v === undefined || v === null || v === ''
    if (CONNECT_FIELDS.includes(f.type) && empty) out[f.key] = `${f.label} is required: pick one.`
    else if (f.required && empty && (isNew || !f.secret)) out[f.key] = `${f.label} is required.`
    else if (!empty && f.pattern && !new RegExp(f.pattern).test(String(v))) out[f.key] = `${f.label} does not look right.`
  })
  return out
}

// The config a draft may keep while the person is away at a consent page:
// every plain field, never a secret.
const plainConfig = (schema, value) => {
  const secret = new Set(schema.filter(f => f.secret).map(f => f.key))
  return Object.fromEntries(Object.entries(value || {}).filter(([k]) => !secret.has(k)))
}

// The stored name of a channel field's channel (channel_id -> channel_name),
// which the server writes beside it.
export const channelNameKey = key => `${key.replace(/_id$/, '')}_name`

// The admin's words for ConfigSchema values core names in its own terms
// (owner 2026-10-30: the "Send as" select showed "together" / "each"): a
// select shows these, and keeps sending the stored values.
export const OPTION_WORDS = {
  email_mode: { each: 'One email per address', together: 'One email to everyone' },
  body_style: { envelope: 'Standard', flat: 'Flat (for Zapier and Slack workflows)' }
}
export const optionText = (field, option) => ((OPTION_WORDS[field.key] || {})[option]) || option
// Help the admin writes itself in place of the server's: null drops a line
// the words above already say. A channel field's help is the picker's own
// private channel hint (channel-picker.js: core's names a bot, whose name
// differs per environment), never the server's.
export const HELP_WORDS = { email_mode: null, body_style: null }
export const helpOf = field => (Object.prototype.hasOwnProperty.call(HELP_WORDS, field.key) ? HELP_WORDS[field.key] : field.help)

// SchemaForm draws a target's config form from its action type's
// ConfigSchema (spec §12.1): a new channel type needs no admin code. Secret
// fields are write-only: once set they show "set" and can only be replaced
// (the target drawer's secret panel does that). Generated secrets (signing
// secrets) are created by the server and shown once.
//
// A `connection` field is the connected-workspace field (connection-field.js)
// and a `channel` field the picker of its connection's channels
// (channel-picker.js; connectionKey names the connection field). connect is
// what they need from the page: editable (may connect, reconnect, create a
// channel), inline (the route editor: never leaves the page), ready (the
// action type's: false until every service can use it), returnTo and
// draftName (the target's name) for the way back from a consent page.
// hideSecrets: a saved target's page shows its secrets in a section of their
// own, so the form leaves them out.
export const SchemaForm = ({ schema, value, secretsSet = {}, onChange, isNew, errors = {}, connect = {}, hideSecrets = false }) => {
  const editable = connect.editable !== false
  const inline = Boolean(connect.inline)
  const pending = { returnTo: connect.returnTo, draft: { name: connect.draftName || '', config: plainConfig(schema, value) } }
  return (
    <div className="ar-form">
      {schema.filter(f => f.type !== 'email_list').map(f => {
        const id = `ar-cfg-${f.key}`
        const problem = errors[f.key] && <div className="invalid-feedback d-block">{errors[f.key]}</div>
        if (f.type === 'connection') {
          // another workspace: the channels that depended on it are picked again
          const dependents = schema.filter(g => g.type === 'channel' && g.connectionKey === f.key)
          const pick = x => onChange({ ...value, [f.key]: x, ...(x === value[f.key] ? {} : Object.fromEntries(dependents.map(g => [g.key, '']))) })
          return (
            <div className="sw-field" key={f.key}>
              <ConnectionField id={id} field={f} value={value[f.key]} invalid={Boolean(errors[f.key])} onChange={pick}
                editable={editable} inline={inline} ready={connect.ready !== false} pending={pending} />
              {helpOf(f) && <div className="sw-hint">{helpOf(f)}</div>}
              {problem}
            </div>
          )
        }
        if (f.type === 'channel') {
          const connection = schema.find(g => g.key === f.connectionKey)
          return (
            <div className="sw-field" key={f.key}>
              <label className="sw-label ar-required" htmlFor={id}>{f.label}</label>
              <ChannelPicker id={id} label={f.label} connectionID={value[f.connectionKey]} value={value[f.key] || ''} knownName={value[channelNameKey(f.key)]}
                invalid={Boolean(errors[f.key])} onChange={x => onChange({ ...value, [f.key]: x })} editable={editable} inline={inline}
                fieldKey={f.key} pending={{ ...pending, provider: connection && connection.provider }} />
              {problem}
            </div>
          )
        }
        if (f.secret && hideSecrets) return null
        if (f.secret && (secretsSet[f.key] || (f.generate && isNew))) {
          return <p key={f.key} className="sw-hint m-0">{f.label}: {secretsSet[f.key] ? 'set' : 'generated when you save, and shown once'}</p>
        }
        if (f.secret && !isNew) return null
        const v = value[f.key] === undefined ? (f.default === undefined ? '' : f.default) : value[f.key]
        const set = x => onChange({ ...value, [f.key]: x })
        return (
          <div className="sw-field" key={f.key}>
            {f.type === 'bool' ? (
              <div className="form-check">
                <input id={id} className="form-check-input" type="checkbox" checked={Boolean(v)} onChange={e => set(e.target.checked)} />
                <label className="form-check-label" htmlFor={id}>{f.label}</label>
              </div>
            ) : (
              <>
                <label className={`sw-label${f.required ? ' ar-required' : ''}`} htmlFor={id}>{f.label}</label>
                {f.type === 'enum' ? (
                  <select id={id} className="form-select" required={Boolean(f.required)} value={v} onChange={e => set(e.target.value)}>
                    {(f.options || []).map(o => <option key={o} value={o}>{optionText(f, o)}</option>)}
                  </select>
                ) : (
                  <input id={id} className={`form-control${errors[f.key] ? ' is-invalid' : ''}`} type={inputType[f.type] || 'text'}
                    required={Boolean(f.required)} autoComplete={f.secret ? 'new-password' : 'off'} value={v}
                    onChange={e => set(f.type === 'int' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
                )}
              </>
            )}
            {helpOf(f) && <div className="sw-hint">{helpOf(f)}</div>}
            {problem}
          </div>
        )
      })}
    </div>
  )
}
